import { api, apiSchemas } from '@verifime/api-definition';
import { useEffect, useState } from 'react';
import { z } from 'zod';

/**
 * What one axis of the confidence display knows about itself.
 *
 * Four states, not three. `unavailable` is separate from `error` because nothing failed: the call
 * succeeded and simply carried no level, which is what the contract returns while the exposure
 * toggle is off and whenever a level cannot be determined. Folding the two together at this layer
 * would report a failure that did not happen, or hide one that did, to any future caller that needs
 * to tell them apart.
 *
 * **The distinction stops here as of verifime-adr#264.** It used to also mean the two rendered
 * differently: an absent card against one saying it could not be loaded. A blank card carrying only
 * a "Last updated on" caption with no date turned out to be the worse failure mode of the two, so
 * `CustomerConfidenceCards` now routes both `unavailable` and `error` into the one existing failure
 * treatment. That is a views-layer rendering choice, not a change to what this hook reports: the
 * data model above still keeps the two states apart, and the tests in `useCustomerConfidence.test.ts`
 * pin that.
 *
 * Neither is a level. `NotCompleted` says the customer has not done the check, which is a fact about
 * them; these are facts about our request.
 *
 * **`TReadyDetail` is what one axis knows that the other does not**, and it is empty by default, so
 * an axis with nothing extra to say keeps exactly the shape above. See `TDocumentAxisState` for the
 * one axis that uses it and why the alternative, a shared optional field, was rejected.
 */
export type TConfidenceAxisState<TLevel extends string, TReadyDetail = unknown> =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'unavailable' }
  | ({ status: 'ready'; level: TLevel; lastUpdatedOn: string } & TReadyDetail);

/** The document axis's levels, as the endpoint spells them. */
export type TDocumentLevel = 'NotCompleted' | 'Low' | 'Substantial' | 'High' | 'VeryHigh';

/** The biometric axis's levels, as the endpoint spells them. */
export type TBiometricLevel = 'NotCompleted' | 'InProgress' | 'Low' | 'High';

/**
 * An identity document type the response can name behind a document level.
 *
 * **Taken from the generated schema rather than written out.** Unlike the two level unions above,
 * this one is reconciled against a second vocabulary downstream: `@verifime/components` matches the
 * KYC table's rows against `@verifime/utils`' own `TIdentityDocumentType`, which knows nothing about
 * this API. Deriving this end of the pair from the contract itself is what makes an upstream rename
 * or addition of a document type a compile error in `documentsDecreasingConfidence`, which is in
 * this layer, rather than a mark that quietly stops appearing in a layer that must not know the
 * contract at all. The field that carries these is pinned separately; see
 * `TDocumentConfidenceCategory`.
 */
export type TProblemDocumentType = z.infer<typeof apiSchemas.IdentityDocumentType>;

/**
 * The document axis, which carries one thing the biometric axis cannot.
 *
 * **Why a type parameter rather than a field on the shared state.** `problemDocumentTypes` exists
 * only on the document category of the response: the biometric check has no documents behind it,
 * so a biometric state carrying this field would advertise something that can never be populated,
 * and every biometric caller would have to keep answering "no, not on this axis" for the life of
 * the type. Parameterising the settled state instead leaves the four states, their names and their
 * shape shared by both axes, and makes the document only field unreachable from the biometric one
 * by construction rather than by convention.
 *
 * **On the settled state alone**, for the same reason `level` is: a loading, failed or unavailable
 * axis has no outcome, so it has no documents behind that outcome to name. Putting the list beside
 * the level rather than at the top of the union is what stops a view marking rows under an outcome
 * the client was never shown.
 *
 * **Optional, and absent is unknown rather than clean.** Absence is "we were not told", which an
 * older backend and a level that could not be broken down both produce. An empty list would be the
 * positive claim "no document is causing this". Reading the first as the second is the one failure
 * that makes the mark untrustworthy, because it is the reading that stays silent when we do not
 * know, so the two are kept apart here rather than collapsed.
 *
 * **Only absence occurs in practice.** The producer sends the list when, and only when, the level
 * is `Low`, and omits the key rather than sending `[]`, so nothing upstream exercises the empty
 * case. The distinction is held for robustness, not because the endpoint walks both paths, and
 * both render no mark either way; what differs is only what a later reader may conclude.
 */
export type TDocumentAxisState = TConfidenceAxisState<
  TDocumentLevel,
  { problemDocumentTypes?: TProblemDocumentType[] }
>;

/** The biometric axis, whose settled state carries nothing beyond the level and its timestamp. */
export type TBiometricAxisState = TConfidenceAxisState<TBiometricLevel>;

export type TUseCustomerConfidenceParams = {
  customerId?: string;
  /**
   * Load-bearing, not optional detail. The document level is only meaningful against an
   * organisation's required document count, so the endpoint is organisation-scoped and there is no
   * unscoped variant to fall back to.
   */
  organisationId?: string;
  /**
   * Called when the request fails. This package has no logging surface of its own, so without it a
   * failure is visible on screen but unobservable anywhere else.
   */
  onError?: (error: unknown) => void;
};

export type TUseCustomerConfidence = {
  document: TDocumentAxisState;
  biometric: TBiometricAxisState;
};

const LOADING = { status: 'loading' } as const;
const ERROR = { status: 'error' } as const;
const UNAVAILABLE = { status: 'unavailable' } as const;

/**
 * Turns one category of the response into the state its card renders.
 *
 * A category that is absent, or present without a level, both resolve to `unavailable`: they are
 * the same absence expressed two ways and the card has nothing to draw in either case.
 */
function axisState<TLevel extends string>(category?: {
  level?: string;
  lastUpdatedOn?: string;
}): TConfidenceAxisState<TLevel> {
  if (!category?.level) {
    return UNAVAILABLE;
  }

  return {
    status: 'ready',
    level: category.level as TLevel,
    lastUpdatedOn: category.lastUpdatedOn,
  };
}

/**
 * The document category of the confidence response, exactly as the generated client hands it over.
 *
 * **Read off the contract rather than written out, because a hand written parameter here is not a
 * check.** The generated schema is `.partial().passthrough()`, so a response that respelled a field
 * still satisfies a literal type naming the old spelling: the property is optional, the argument is
 * not a fresh object literal, and TypeScript has nothing to object to. Nothing else in the feature
 * would object either, because the tests and the stories feed hand written fixtures rather than the
 * schema, so the mark would simply stop appearing with every gate still green.
 *
 * Taking the type from `IdentityConfidenceResponse` is what makes the field name below load
 * bearing: rename `problemDocumentTypes` upstream, regenerate, and this file stops compiling.
 */
type TDocumentConfidenceCategory = z.infer<
  typeof apiSchemas.IdentityConfidenceResponse
>['Document'];

/**
 * The document category, with the documents it named behind its level.
 *
 * **The list rides the level, and only the level.** It arrives on the same category of the same
 * response, so a settled level is exactly the state in which there is something to name; a
 * category that carried a list but no level is a contradiction in the response, and taking the
 * list from it would let a view mark rows under an outcome the client was never shown.
 *
 * **Copied across only when the response actually carried it.** Substituting an empty list for an
 * absent field would turn "we were not told" into the claim "no document is causing this level",
 * which is precisely the reading this feature must not make: it is the one that stays quiet when we
 * do not know. Absence stays absence, `[]` stays `[]`, and the table draws nothing for either. Only
 * the first of those two arrives from the producer today; see `TDocumentAxisState`.
 */
function documentAxisState(category?: TDocumentConfidenceCategory): TDocumentAxisState {
  const state = axisState<TDocumentLevel>(category);

  if (state.status !== 'ready' || !category?.problemDocumentTypes) {
    return state;
  }

  return { ...state, problemDocumentTypes: category.problemDocumentTypes };
}

/**
 * Reads the customer's confidence levels for one organisation.
 *
 * **Both axes share one request and therefore one failure.** They come from a single endpoint, so
 * there is no state in which one axis errored and the other did not, and pretending otherwise would
 * invite a view to render a half-failure that cannot occur.
 */
export function useCustomerConfidence({
  customerId,
  organisationId,
  onError,
}: TUseCustomerConfidenceParams): TUseCustomerConfidence {
  const [state, setState] = useState<TUseCustomerConfidence>({
    document: LOADING,
    biometric: LOADING,
  });

  useEffect(() => {
    // Reset first. The `abandoned` flag below stops a late response overwriting a newer one; it does
    // nothing about the interim, during which the previous customer's levels would still be on
    // screen under the new customer's name.
    setState({ document: LOADING, biometric: LOADING });

    // Both ids arrive from routing and context and either can be undefined on the first render.
    // Calling with an undefined path parameter builds a malformed URL, which answers 404 and would
    // surface as a failure the client can neither understand nor act on.
    if (!customerId || !organisationId) {
      return;
    }

    let abandoned = false;

    api
      .getV1personCustomerIdorganisationOrganisationIdverificationconfidence({
        params: { customerId, organisationId },
      })
      .then((response) => {
        if (abandoned) {
          return;
        }

        setState({
          document: documentAxisState(response?.Document),
          // Deliberately the plain mapper, which builds a fresh state from the level and its
          // timestamp alone. A response that put `problemDocumentTypes` on this category could only
          // have done so by mistake, and it is dropped here rather than carried to a view that has
          // no documents to attach it to.
          biometric: axisState<TBiometricLevel>(response?.Biometric),
        });
      })
      .catch((error: unknown) => {
        if (abandoned) {
          return;
        }

        setState({ document: ERROR, biometric: ERROR });
        onError?.(error);
      });

    // Guards against a resolution landing after the customer has been navigated away from, which
    // would otherwise show one customer's levels under another customer's name.
    return () => {
      abandoned = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, organisationId]);

  return state;
}
